import { Stack, StackProps, FormControl, FormLabel, FormHelperText, RadioGroup } from "@mui/material";
import { useFormContext } from "@/components/form/contexts/form-context";

export interface FormRadioButtonGroupProps extends Omit<StackProps, 'onChange'> {
    name: string;
    label?: string;
    helperText?: string;
    row?: boolean;
    defaultValue?: string;
    onChange?: (value: string) => void;
}

export default function FormRadioButtonGroup({
    name,
    label,
    helperText,
    children,
    row = true,
    defaultValue = '',
    onChange,
    ...props
}: FormRadioButtonGroupProps) {
    const form = useFormContext();
    
    return (
        <form.Field name={name}>
            {(field) => {
                const value = field.state.value ?? defaultValue;
                
                // Initialize form field with default value if not set
                if (field.state.value === undefined && defaultValue) {
                    form.setFieldValue(name, defaultValue);
                }

                const error = field.state.meta.errors?.[0];

                const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                    const newValue = event.target.value;
                    field.handleChange(newValue);
                    onChange?.(newValue);
                };

                return (
                    <FormControl component="fieldset" error={!!error} fullWidth>
                        {label && (
                            <FormLabel component="legend" sx={{ mb: 1, color: 'text.primary' }}>
                                {label}
                            </FormLabel>
                        )}
                        <RadioGroup
                            name={name}
                            value={value || ''}
                            onChange={handleChange}
                            row={row}
                        >
                            <Stack 
                                direction={row ? 'row' : 'column'} 
                                spacing={2} 
                                {...props}
                                sx={{ 
                                    ...(props.sx || {}),
                                    flexWrap: 'wrap',
                                    '& .MuiFormControlLabel-root': {
                                        mr: 2,
                                        mb: 1
                                    }
                                }}
                            >
                                {children}
                            </Stack>
                        </RadioGroup>
                        {(error || helperText) && (
                            <FormHelperText>{typeof error === 'string' ? error : (error as any)?.message || helperText}</FormHelperText>
                        )}
                    </FormControl>
                );
            }}
        </form.Field>
    );
}